import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { useFormContext } from "react-hook-form";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  AI_GENERATED_SECTION_CHIP,
  AI_GENERATED_SECTION_CHIP_ARIA,
} from "../../i18n/keysTranslations";
import {
  useAIGeneratedAnySection,
  useAIGeneratedSection,
} from "../../hooks/useAIGeneratedSection";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  AI_GENERATED_CHIP_BOX_SHADOW,
  AI_GENERATED_CHIP_COLOR_TEXT,
  GRADIENT_AI_GENERATED_CHIP,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
  buildAIGeneratedChipBackground,
} from "../../utils/colors";
import { IS_WHITE_LABEL_BRAND } from "../../utils/constants";

const EMPTY_SX = {};
const CHIP_AI_GENERATED_CLASS = "chip-ai-generated";

/** Overrides theme MuiSvgIcon (PRIMARY_COLOR_TEXT !important) inside this chip. */
const chipIconWhiteSx = {
  [`&.${CHIP_AI_GENERATED_CLASS} .MuiChip-icon`]: {
    color: `${AI_GENERATED_CHIP_COLOR_TEXT} !important`,
  },
  [`&.${CHIP_AI_GENERATED_CLASS} .MuiChip-icon .MuiSvgIcon-root`]: {
    color: `${AI_GENERATED_CHIP_COLOR_TEXT} !important`,
  },
  [`&.${CHIP_AI_GENERATED_CLASS} .MuiChip-icon path`]: {
    fill: "currentColor",
  },
};

const ChipAIGenerated = ({ size = "small", sx = EMPTY_SX }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const theme = useTheme();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const primary =
    typeof theme.palette?.primary?.main === "string"
      ? theme.palette.primary.main
      : PRIMARY_COLOR;
  const secondary =
    typeof theme.palette?.secondary?.main === "string"
      ? theme.palette.secondary.main
      : SECONDARY_COLOR;
  const label = t(AI_GENERATED_SECTION_CHIP, { NAME_BUSINESS });
  const ariaLabel = t(AI_GENERATED_SECTION_CHIP_ARIA, { NAME_BUSINESS });
  const background = IS_WHITE_LABEL_BRAND
    ? buildAIGeneratedChipBackground(primary, secondary)
    : GRADIENT_AI_GENERATED_CHIP;

  return (
    <Tooltip title={ariaLabel}>
      <Chip
        className={CHIP_AI_GENERATED_CLASS}
        size={size}
        icon={<AutoAwesomeIcon fontSize="small" />}
        label={label}
        aria-label={ariaLabel}
        sx={{
          background,
          color: AI_GENERATED_CHIP_COLOR_TEXT,
          fontWeight: 600,
          letterSpacing: "0.01em",
          height: 24,
          boxShadow: AI_GENERATED_CHIP_BOX_SHADOW,
          "& .MuiChip-label": {
            fontSize: "0.8125rem",
            lineHeight: 1.43,
            px: 1,
          },
          ...chipIconWhiteSx,
          ...sx,
        }}
      />
    </Tooltip>
  );
};

const ChipAIGeneratedSectionInForm = ({ control, basePath, section }) => {
  const showChip = useAIGeneratedSection({ control, basePath, section });
  if (!showChip) return null;
  return <ChipAIGenerated />;
};

// Renders nothing outside a react-hook-form FormProvider instead of crashing.
export const ChipAIGeneratedSection = ({ basePath, section }) => {
  const formContext = useFormContext();
  if (!formContext?.control || !basePath || !section) return null;
  return (
    <ChipAIGeneratedSectionInForm
      control={formContext.control}
      basePath={basePath}
      section={section}
    />
  );
};

export const ChipAIGeneratedAnySection = ({ basePath, sections }) => {
  const showChip = useAIGeneratedAnySection({ basePath, sections });
  if (!showChip) return null;
  return <ChipAIGenerated />;
};

export default ChipAIGenerated;
